import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, NumberInput, ColorInput, PanelSection } from "./controls";
import { defaultAnimationFor } from "../templates";

const EASING_OPTIONS = [
  { value: "linear", label: "linear" },
  { value: "ease", label: "ease" },
  { value: "ease-in", label: "ease-in" },
  { value: "ease-out", label: "ease-out" },
  { value: "ease-in-out", label: "ease-in-out" },
  { value: "cubic-bezier(.34,1.56,.64,1)", label: "spring" },
  { value: "cubic-bezier(.68,-0.55,.27,1.55)", label: "back" },
  { value: "cubic-bezier(.87,0,.13,1)", label: "expo" },
  { value: "cubic-bezier(.36,0,.66,-0.56)", label: "back-in" },
  { value: "cubic-bezier(.5,1.7,.4,.9)", label: "elastic-out" },
  { value: "cubic-bezier(.165,.84,.44,1)", label: "circ-out" },
  { value: "cubic-bezier(.785,.135,.15,.86)", label: "circ-in-out" },
  { value: "steps(1)", label: "steps" },
  { value: "steps(8,end)", label: "steps-8" },
];
const REPEAT_OPTIONS = [
  { value: "1", label: "Once" },
  { value: "2", label: "2x" },
  { value: "3", label: "3x" },
  { value: "5", label: "5x" },
  { value: "10", label: "10x" },
  { value: "infinite", label: "Infinite" },
];
const DIRECTION_OPTIONS = ["normal", "reverse", "alternate", "alternate-reverse"];
const ORIGIN_OPTIONS = ["center", "top", "bottom", "left", "right", "topLeft", "topRight", "bottomLeft", "bottomRight"];
const FILLMODE_OPTIONS = ["both", "forwards", "backwards", "none"];

const ANIM_GROUPS = [
  { group: "Basic", items: [["none", "None"], ["fade", "Fade"], ["fadeUp", "Fade Up"], ["fadeDown", "Fade Down"], ["fadeLeft", "Fade Left"], ["fadeRight", "Fade Right"], ["popIn", "Pop In"], ["bounceIn", "Bounce In"], ["elastic", "Elastic"], ["zoomIn", "Zoom In"], ["zoomOut", "Zoom Out"], ["blink", "Blink"], ["slideInLeft", "Slide In ←"], ["slideInRight", "Slide In →"]] },
  { group: "Transform", items: [["rotate", "Rotate"], ["scale", "Pulse / Scale"], ["scaleX", "Scale X"], ["scaleY", "Scale Y"], ["translateY", "Bounce Y"], ["translateX", "Slide X"], ["float", "Float"], ["orbit", "Orbit"], ["skew", "Skew"]] },
  { group: "Expressive", items: [["shake", "Shake"], ["wobble", "Wobble"], ["swing", "Swing"], ["jello", "Jello"], ["heartbeat", "Heartbeat"], ["rubberBand", "Rubber Band"], ["tada", "Tada"], ["flip", "Flip Y"], ["flipX", "Flip X"], ["spin3d", "Spin 3D"]] },
  { group: "Path / Color", items: [["drawOn", "Draw On · 绘制过程"], ["draw", "Draw (path/line)"], ["drawReverse", "Undraw"], ["dash", "March (dash)"], ["colorShift", "Color Shift"], ["strokeShift", "Stroke Shift"], ["morphHue", "Hue Cycle"]] },
  { group: "Creative", items: [["rollIn", "Roll In"], ["spiralIn", "Spiral In"], ["dropBounce", "Drop In"], ["pendulum", "Pendulum"], ["wave", "Wave"], ["jiggle", "Jiggle"], ["breathe", "Breathe"], ["glitch", "Glitch"], ["vibrate", "Vibrate"], ["flash", "Flash"]] },
  { group: "3D", items: [["flipInX", "Flip In X"], ["flipInY", "Flip In Y"], ["cube3d", "Cube 3D"]] },
  { group: "FX · Filter", items: [["blurIn", "Blur In"], ["blurPulse", "Blur Pulse"], ["neonGlow", "Neon Glow"], ["brightnessPulse", "Brightness"], ["saturatePulse", "Saturate"]] },
];

const NUM_AMP = new Set(["rotate", "scale", "scaleX", "scaleY", "translateY", "translateX", "shake", "wobble", "swing", "spin3d", "blink", "fadeUp", "fadeDown", "fadeLeft", "fadeRight", "zoomIn", "zoomOut", "orbit", "skew", "pendulum"]);
const NEEDS_COLOR = new Set(["colorShift", "strokeShift"]);

export default function AnimationControls({ animation, setAnim }) {
  const anim = animation;

  const onTypeChange = (v) => {
    if (v === "none") return setAnim(null);
    // Switching types must RESET type-specific fields (from/to/fromColor/
    // toColor/dasharray/glowColor/origin) to the new type's defaults —
    // otherwise leftover fields from the previous type leak into the new
    // animation and produce wrong/blank effects (e.g. rotate's from:0/to:360
    // reused by scale → scale(0)→scale(360) = invisible). Only common timing
    // fields (duration/delay/easing/repeat/direction/fillMode) are carried
    // over so the user's timing tweaks survive a type switch.
    // defaultAnimationFor is shared with the left-panel presets, so applying
    // "Pulse" from either side yields an identical animation object.
    const keepCommon = anim
      ? {
          duration: anim.duration,
          delay: anim.delay,
          easing: anim.easing,
          repeat: anim.repeat,
          direction: anim.direction,
          fillMode: anim.fillMode,
        }
      : undefined;
    setAnim(defaultAnimationFor(v, keepCommon));
  };

  return (
    <PanelSection title="Animation">
      <Field label="Type">
        <Select value={anim?.type || "none"} onValueChange={onTypeChange}>
          <SelectTrigger data-testid="anim-type-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-chrome border-line text-foreground rounded-none max-h-72">
            {ANIM_GROUPS.map((g) => (
              <React.Fragment key={g.group}>
                <div className="px-2 py-1 text-[9px] font-mono uppercase tracking-widest text-ink4">{g.group}</div>
                {g.items.map(([value, label]) => (
                  <SelectItem key={value} value={value}>{label}</SelectItem>
                ))}
              </React.Fragment>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {anim && (
        <>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Field label="Duration (s)">
              <NumberInput value={anim.duration} step={0.1} onChange={(v) => setAnim({ duration: v })} testId="anim-duration-input" />
            </Field>
            <Field label="Delay (s)">
              <NumberInput value={anim.delay} step={0.1} onChange={(v) => setAnim({ delay: v })} testId="anim-delay-input" />
            </Field>
            <Field label="Easing">
              <Select value={anim.easing} onValueChange={(v) => setAnim({ easing: v })}>
                <SelectTrigger data-testid="anim-easing-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {EASING_OPTIONS.map((e) => (<SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Repeat">
              <Select value={String(anim.repeat)} onValueChange={(v) => setAnim({ repeat: v })}>
                <SelectTrigger data-testid="anim-repeat-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {REPEAT_OPTIONS.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Direction">
              <Select value={anim.direction || "normal"} onValueChange={(v) => setAnim({ direction: v })}>
                <SelectTrigger data-testid="anim-direction-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {DIRECTION_OPTIONS.map((d) => (<SelectItem key={d} value={d}>{d}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Origin">
              <Select value={anim.origin || "center"} onValueChange={(v) => setAnim({ origin: v, originX: undefined, originY: undefined })}>
                <SelectTrigger data-testid="anim-origin-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {ORIGIN_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="mt-2">
            <Field label="Fill Mode">
              <Select value={anim.fillMode || "both"} onValueChange={(v) => setAnim({ fillMode: v })}>
                <SelectTrigger data-testid="anim-fillmode-select" className="h-8 rounded-none bg-chrome border-line text-foreground text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-chrome border-line text-foreground rounded-none">
                  {FILLMODE_OPTIONS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {NUM_AMP.has(anim.type) && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <Field label="From">
                <NumberInput value={anim.from} step={0.1} onChange={(v) => setAnim({ from: v })} testId="anim-from-input" />
              </Field>
              <Field label="To">
                <NumberInput value={anim.to} step={0.1} onChange={(v) => setAnim({ to: v })} testId="anim-to-input" />
              </Field>
            </div>
          )}

          {anim.type === "colorShift" || anim.type === "strokeShift" ? (
            <div className="grid grid-cols-1 gap-2 mt-2">
              <Field label="From Color">
                <ColorInput value={anim.fromColor || "#002FA7"} onChange={(v) => setAnim({ fromColor: v })} testId="anim-fromcolor-input" />
              </Field>
              <Field label="To Color">
                <ColorInput value={anim.toColor || "#FF2A00"} onChange={(v) => setAnim({ toColor: v })} testId="anim-tocolor-input" />
              </Field>
            </div>
          ) : null}

          {anim.type === "dash" && (
            <div className="grid grid-cols-1 gap-2 mt-2">
              <Field label="Dash Pattern (e.g. 10 8)">
                <input
                  value={anim.dasharray || "10 8"}
                  onChange={(e) => setAnim({ dasharray: e.target.value })}
                  data-testid="anim-dasharray-input"
                  className="h-8 rounded-none bg-chrome border border-line text-foreground text-sm font-mono px-2 w-full"
                />
              </Field>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 mt-2">
            <Field label="Origin X (px)">
              <NumberInput value={anim.originX} step={1} onChange={(v) => setAnim({ originX: v })} testId="anim-originX-input" />
            </Field>
            <Field label="Origin Y (px)">
              <NumberInput value={anim.originY} step={1} onChange={(v) => setAnim({ originY: v })} testId="anim-originY-input" />
            </Field>
          </div>
        </>
      )}
    </PanelSection>
  );
}
